import {useState, useEffect, useRef, useMemo} from 'react';
import {
  ModestWeb,
  ExperimentAssignment,
  AnalyticsEventPayload,
} from '@lightricks/modest-web';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import Sentry from '@/services/sentry/Sentry';
import getCookieValue from '@/utils/getCookieValue';
import getLtid from '@/utils/getLtid';
import {getEnv} from '@/config/environment';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useSessionId from '@/hooks/use-session-id';
import useToken from '@/hooks/use-token';

function createModestWebInstance(
  appId: string,
  appName: string,
  appVersion: string,
  env: 'prod' | 'dev',
  debugLogger?: (...args: any[]) => void
): ModestWeb {
  const modestWebInstance = new ModestWeb({
    appId,
    appName,
    env,
    context: {
      appVersion,
      anonymousId: getCookieValue('anonymous_id'),
      sessionId: getCookieValue('session_id'),
      ltId: getLtid(),
    },
  });

  setupModestWebEventListeners(modestWebInstance, appId, appName, debugLogger);

  return modestWebInstance;
}

interface UseModestWebInitializerProps {
  appId: string;
  appName: string;
  appVersion?: string;
  env?: 'prod' | 'dev';
  debugLogger: (...args: unknown[]) => void;
  enabled?: boolean;
}

export default function useModestWebInitializer({
  appId,
  appName,
  appVersion = '1.0.0',
  env = getEnv().MODE === 'production' ? 'prod' : 'dev',
  debugLogger,
  enabled = true,
}: UseModestWebInitializerProps) {
  const {isAuthenticated, initialized: tokenInitialized} = useToken(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const modestWebRef = useRef<ModestWeb | null>(null);
  const hasInitializedRef = useRef(false);

  const {dashboardUser}: any = useDashboardUserQuery({
    overrideEnabled: isAuthenticated,
    include: ['current_brand.agency', 'roles'],
  });

  const {sessionId, isInitialized: sessionInitialized} = useSessionId();

  // Memoize values that shouldn't change frequently
  const anonymousId = useMemo(() => getCookieValue('anonymous_id'), []);
  const memoizedSessionId = useMemo(() => sessionId, [sessionId]);
  const isLtTeam = useMemo(() => !!getCookieValue('is_lt_team'), []);

  // Memoize the context object to prevent unnecessary updates
  const userContext = useMemo(() => {
    const ltId = getLtid();
    if (isAuthenticated && dashboardUser?.email && ltId) {
      return {
        appId,
        appName,
        appVersion,
        brandId: dashboardUser.currentBrand?.id,
        anonymousId,
        ltId,
        organizationId: dashboardUser.currentBrand?.agency.id,
        isLtTeam,
        sessionId: memoizedSessionId || undefined,
      };
    }
    if (!isAuthenticated) {
      return {
        appId,
        appName,
        appVersion,
        brandId: undefined,
        anonymousId,
        ltId: undefined,
        organizationId: undefined,
        isLtTeam,
        sessionId: memoizedSessionId || undefined,
      };
    }
    return null;
  }, [
    isAuthenticated,
    dashboardUser,
    appId,
    appName,
    appVersion,
    anonymousId,
    memoizedSessionId,
    isLtTeam,
  ]);

  // Initialize ModestWeb once when token and session are ready
  useEffect(() => {
    if (
      !enabled ||
      !tokenInitialized ||
      !sessionInitialized ||
      !appId ||
      !appName ||
      hasInitializedRef.current
    ) {
      if (!enabled) {
        debugLogger('ModestWeb: Skipping initialization - disabled');
        setIsInitialized(true); // Set as initialized when disabled to not block app loading
        return;
      }
      if (!tokenInitialized || !sessionInitialized || !appId || !appName) {
        debugLogger(
          'ModestWeb: Skipping initialization - missing required config:',
          {
            tokenInitialized,
            sessionInitialized,
            appId,
            appName,
          }
        );
      }
      return;
    }

    const initializeModestWeb = async () => {
      try {
        debugLogger('ModestWeb: Starting initialization for', {
          appId,
          appName,
        });
        setIsFetching(true);
        hasInitializedRef.current = true;

        const modestWeb = createModestWebInstance(
          appId,
          appName,
          appVersion,
          env,
          debugLogger
        );
        modestWebRef.current = modestWeb;

        // Fetch experiment configurations
        debugLogger('ModestWeb: Fetching experiments...');
        await modestWeb.fetch();
        debugLogger('ModestWeb fetched successfully');

        setIsInitialized(true);
        debugLogger('ModestWeb: Initialization complete');
      } catch (error) {
        Sentry.captureException(error as Error, {
          extra: {
            appId,
            appName,
          },
        });
        hasInitializedRef.current = false; // Allow retry on error
      } finally {
        setIsFetching(false);
      }
    };

    initializeModestWeb();
  }, [
    enabled,
    tokenInitialized,
    sessionInitialized,
    appId,
    appName,
    debugLogger,
    appVersion,
    env,
  ]);

  // Update user context and evaluate experiments when authentication state changes
  useEffect(() => {
    if (!enabled || !modestWebRef.current || !isInitialized || !userContext) {
      if (!enabled) {
        debugLogger('ModestWeb: Skipping context update - disabled');
        return;
      }
      if (!userContext) {
        debugLogger(
          'ModestWeb: Skipping context update - no user context available'
        );
      }
      return;
    }

    const modestWeb = modestWebRef.current;
    const currentContext = modestWeb.getUserContext();

    // Only update if context has actually changed
    const currentContextString = JSON.stringify(currentContext);
    const userContextString = JSON.stringify(userContext);

    if (currentContextString !== userContextString) {
      modestWeb.updateContext(userContext);
      debugLogger(
        `ModestWeb: Updated context for ${
          isAuthenticated ? 'authenticated' : 'anonymous'
        } user`
      );
      debugLogger('ModestWeb: Context changed from:', currentContext);
      debugLogger('ModestWeb: Context changed to:', userContext);

      // Evaluate experiments only when context was actually updated
      if (!isFetching) {
        try {
          modestWeb.evaluate(userContext as any);
          debugLogger(
            'ModestWeb: Evaluated experiments with attributes:',
            userContext
          );
        } catch (error) {
          Sentry.captureException(error as Error, {
            extra: {
              appId,
              appName,
            },
          });
        }
      }
    } else {
      debugLogger(
        `ModestWeb: Context unchanged for ${
          isAuthenticated ? 'authenticated' : 'anonymous'
        } user`
      );
    }
  }, [
    enabled,
    appId,
    appName,
    userContext,
    isInitialized,
    isAuthenticated,
    debugLogger,
    isFetching,
  ]);

  return {
    modestWeb: modestWebRef.current,
    modestInitialized: isInitialized,
    modestFetching: isFetching,
    dashboardUser: dashboardUser?.loaded ? dashboardUser : null,
  };
}

function setupModestWebEventListeners(
  modestWebInstance: ModestWeb,
  appId: string,
  appName: string,
  debugLogger?: (...args: any[]) => void
): void {
  modestWebInstance.on(
    'experiment:assignment',
    (result: ExperimentAssignment) => {
      debugLogger?.(
        `🎯 ModestWeb: Assigned to: ${result.experimentGroup.name}`
      );
      debugLogger?.(`   Variant: ${result.variant.displayName}`);
      debugLogger?.(`   Config:`, result.variant.configuration);
    }
  );

  modestWebInstance.on(
    'analytics:assignment',
    (event: AnalyticsEventPayload) => {
      debugLogger?.('ModestWeb analytics:assignment', event);
      AnalyticsService.dispatchEvent(event.name, event.properties);
    }
  );

  modestWebInstance.on('experiment:restore', (result: ExperimentAssignment) => {
    debugLogger?.(
      `🔄 ModestWeb: Restored assignment to: ${result.experimentGroup.name}`
    );
  });

  modestWebInstance.on('analytics:restore', (event: AnalyticsEventPayload) => {
    debugLogger?.('ModestWeb analytics:restore', event);
    // Forward to DeltaClient via AnalyticsService
    AnalyticsService.dispatchEvent(event.name, event.properties);
  });

  modestWebInstance.on(
    'experiment:unassigned',
    (result: ExperimentAssignment) => {
      debugLogger?.(
        `❌ ModestWeb: Unassigned from: ${result.experimentGroup.name}`
      );
    }
  );

  modestWebInstance.on(
    'analytics:unassignment',
    (event: AnalyticsEventPayload) => {
      debugLogger?.('ModestWeb analytics:unassignment', event);
      // Forward to DeltaClient via AnalyticsService
      AnalyticsService.dispatchEvent(event.name, event.properties);
    }
  );

  modestWebInstance.on('error', (error: Error) => {
    Sentry.captureException(error, {
      extra: {
        appId,
        appName,
      },
    });
  });
}
